/**
 * Step 3 of 4 — Topic Planner.
 *
 * Loads timeline either from:
 *   - Step 2 handoff (location.state.fromCurriculum), or
 *   - topics seeded in Step 1 (sessionStorage)
 * Persists edits on every change. Footer → Step 4 (duration confirm).
 *
 * Pre/post quiz rules are one shared platform config (codelive_quiz_defaults).
 * Topics only enable/disable pre/post — they do not store level rules.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AppLayout, PageMain } from '../components/WizardShell';
import { ROUTES } from '../utils/createRoutes';
import { SessionBanner } from '../components/topicPlanner/SessionBanner';
import { TopicTimeline } from '../components/topicPlanner/TopicTimeline';
import { PlannerFooter } from '../components/topicPlanner/PlannerFooter';
import { questionBankOptions } from '../data/topicPlannerConfig';
import type { TimelineTopicItem } from '../types/topicQuiz';
import { calcSessionDuration, normalizeTopicQuizPlan } from '../types/topicQuiz';
import {
  curriculumPathToTimelineTopics,
  loadCurriculumPath,
  mergeCurriculumIntoPlannerTopics,
} from '../utils/curriculumPathStorage';
import {
  isAdminCreateSessionFlow,
  loadCreateSessionDraft,
  saveCreateSessionDraft,
} from '../utils/createSessionFlowStorage';
import { loadPlannerTopics, savePlannerTopics } from '../utils/topicPlannerStorage';
import {
  type CodeliveQuizDefaults,
  withGlobalQuizRules,
} from '../utils/codeliveQuizDefaults';
import { isWorkshopCategory } from '../utils/sessionCategoryUtils';
import {
  groupTopicsByWorkshopDay,
  mergeDayTimings,
  type WorkshopDayTiming,
} from '../utils/workshopSessionUtils';

export function TopicPlanner() {
  const location = useLocation();
  const [topics, setTopics] = useState<TimelineTopicItem[]>(() =>
    normalizeTopicQuizPlan(loadPlannerTopics()),
  );
  const [quizDefaults, setQuizDefaults] = useState<CodeliveQuizDefaults | null>(null);

  const adminCreateFlow = isAdminCreateSessionFlow();
  const createDraft = adminCreateFlow ? loadCreateSessionDraft() : null;
  const isWorkshop = isWorkshopCategory(createDraft?.sessionCategory);
  const workshopDayCount = Math.max(1, createDraft?.workshopDays || 1);
  const [daySchedules, setDaySchedules] = useState<WorkshopDayTiming[]>(() =>
    mergeDayTimings(
      workshopDayCount,
      createDraft?.startDate || '',
      createDraft?.startTime || '',
      createDraft?.daySchedules,
    ),
  );

  //-------------------------------------------------
  // step:1 hydrate timeline from Prepare handoff or stored planner topics
  //-------------------------------------------------
  useEffect(() => {
    if (location.pathname !== ROUTES.topicPlanner) return;
    const fromCurriculum = Boolean(
      (location.state as { fromCurriculum?: boolean } | null)?.fromCurriculum,
    );
    if (fromCurriculum) {
      // Peek path — do not delete so Prepare still restores on back.
      const path = loadCurriculumPath();
      if (path.length > 0) {
        setTopics(
          mergeCurriculumIntoPlannerTopics(
            curriculumPathToTimelineTopics(path),
            loadPlannerTopics(),
          ),
        );
      } else {
        setTopics(normalizeTopicQuizPlan(loadPlannerTopics()));
      }
    } else {
      setTopics(normalizeTopicQuizPlan(loadPlannerTopics()));
    }
    const draft = loadCreateSessionDraft();
    if (isWorkshopCategory(draft?.sessionCategory)) {
      setDaySchedules(
        mergeDayTimings(
          Math.max(1, draft?.workshopDays || 1),
          draft?.startDate || '',
          draft?.startTime || '',
          draft?.daySchedules,
        ),
      );
      // Ensure day assignment exists (from draft dayTopics) for every topic
      if (draft?.dayTopics?.length) {
        const titleToDay = new Map<string, number>();
        draft.dayTopics.forEach((day, dayIndex) => {
          for (const title of day) {
            titleToDay.set(String(title).trim().toLowerCase(), dayIndex);
          }
        });
        setTopics((prev) =>
          prev.map((t) => {
            if (typeof t.workshopDayIndex === 'number') return t;
            const day = titleToDay.get(t.title.trim().toLowerCase());
            return day === undefined
              ? t
              : {
                  ...t,
                  workshopDayIndex: day,
                  description: t.description || `Workshop Day ${day + 1}`,
                };
          }),
        );
      }
    }
  }, [location.pathname, location.key, location.state]);

  //-------------------------------------------------
  // step:2 keep planner storage in sync for Step 4 duration calc
  //-------------------------------------------------
  useEffect(() => {
    savePlannerTopics(topics);
  }, [topics]);

  const persistDaySchedules = useCallback(
    (
      dayIndex: number,
      patch: { startDate?: string; startTime?: string },
    ) => {
      setDaySchedules((prev) => {
        const draft = loadCreateSessionDraft();
        const base = mergeDayTimings(
          workshopDayCount,
          draft?.startDate || '',
          draft?.startTime || '',
          prev,
        );
        const next = base.map((day) =>
          day.dayIndex === dayIndex
            ? {
                ...day,
                startDate: patch.startDate ?? day.startDate,
                startTime: patch.startTime ?? day.startTime,
              }
            : day,
        );
        if (draft) {
          saveCreateSessionDraft({
            ...draft,
            daySchedules: next,
            dayTopics: groupTopicsByWorkshopDay(topics, workshopDayCount),
            startDate: next[0]?.startDate || draft.startDate,
            startTime: next[0]?.startTime || draft.startTime,
          });
        }
        return next;
      });
    },
    [topics, workshopDayCount],
  );

  const onQuizDefaultsChange = useCallback((defaults: CodeliveQuizDefaults) => {
    setQuizDefaults(defaults);
  }, []);

  const duration = useMemo(
    () =>
      calcSessionDuration(
        withGlobalQuizRules(topics, quizDefaults) as TimelineTopicItem[],
        questionBankOptions,
      ),
    [topics, quizDefaults],
  );
  const sessionTitle = createDraft?.autoSessionName || 'Untitled session';
  const sessionBatch = createDraft?.batchLabel || '—';
  const sessionStudents = createDraft?.studentCount ?? 0;
  const structureLocked = Boolean(createDraft?.fromCoursePlan);

  return (
    <AppLayout>
      <PageMain>
        <SessionBanner
          label="CURRENT SESSION"
          title={sessionTitle}
          batch={sessionBatch}
          studentCount={sessionStudents}
          duration={duration}
          topics={topics}
        />

        <TopicTimeline
          topics={topics}
          duration={duration}
          onTopicsChange={setTopics}
          courseId={createDraft?.courseId}
          onQuizDefaultsChange={onQuizDefaultsChange}
          workshopMode={isWorkshop}
          workshopDayCount={workshopDayCount}
          workshopStartDate={createDraft?.startDate || ''}
          workshopStartTime={createDraft?.startTime || ''}
          daySchedules={daySchedules}
          onDayScheduleChange={isWorkshop ? persistDaySchedules : undefined}
          onChangeTopicDay={
            isWorkshop && !structureLocked
              ? (topicId, dayIndex) => {
                  setTopics((prev) => {
                    const next = prev.map((t) =>
                      t.id === topicId
                        ? {
                            ...t,
                            workshopDayIndex: dayIndex,
                            description: `Workshop Day ${dayIndex + 1}`,
                          }
                        : t,
                    );
                    const sorted = [...next].sort(
                      (a, b) =>
                        (a.workshopDayIndex ?? 0) -
                          (b.workshopDayIndex ?? 0) ||
                        a.index - b.index,
                    );
                    return sorted.map((t, i) => ({ ...t, index: i + 1 }));
                  });
                }
              : undefined
          }
          structureLocked={structureLocked}
        />

        <PlannerFooter
          topics={topics}
          onBeforeContinue={
            isWorkshop
              ? () => {
                  const draft = loadCreateSessionDraft();
                  if (!draft) return;
                  saveCreateSessionDraft({
                    ...draft,
                    daySchedules,
                    dayTopics: groupTopicsByWorkshopDay(
                      topics,
                      workshopDayCount,
                    ),
                    startDate:
                      daySchedules[0]?.startDate || draft.startDate,
                    startTime:
                      daySchedules[0]?.startTime || draft.startTime,
                  });
                }
              : undefined
          }
        />
      </PageMain>
    </AppLayout>
  );
}
